/**
 * Parsing, serialization, and labels for staff role-baseline simulation
 * (session assignment subsets and role templates).
 */

import type { Property, UserSession } from '@/api/client';
import { getRoleTemplateLabel } from '@/lib/auth/role-permission-templates';

/** Default CASL baseline: merged permissions from all session role assignments. */
export const DEFAULT_ROLE_BASELINE = 'session:merged';

type UserRoles = UserSession['roles'];

export const SESSION_BASELINE_PREFIX = 'session:';
export const TEMPLATE_BASELINE_PREFIX = 'template:';

function formatCustomRoleDisplay(
  roleValue: string,
  label?: string | null
): string {
  if (label) return label;
  return roleValue
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatSessionRoleScope(
  propertyId: string | null | undefined,
  properties: Property[] | undefined
): string {
  if (!propertyId) return 'All properties';
  const property = properties?.find((entry) => entry.id === propertyId);
  return property?.name ?? propertyId;
}

export function formatSessionRoleAssignment(
  role: NonNullable<UserRoles>[number],
  properties: Property[] | undefined
): string {
  return `${formatCustomRoleDisplay(role.role, role.label)} — ${formatSessionRoleScope(role.property_id, properties)}`;
}

/** Human-readable label for the user's actual session roles (no simulation). */
export function formatDefaultSessionRolesLabel(
  roles: UserRoles | null | undefined,
  properties: Property[] | undefined
): string {
  const list = roles ?? [];
  if (list.length === 0) return 'No role assignments';
  if (list.length === 1) {
    return formatSessionRoleAssignment(list[0], properties);
  }
  const formatted = list.map((role) =>
    formatSessionRoleAssignment(role, properties)
  );
  if (formatted.length <= 2) {
    return `${formatted.join(' + ')} (merged)`;
  }
  return `${formatted[0]} + ${formatted.length - 1} more roles (merged)`;
}

export function allSessionRoleIndices(roleCount: number): number[] {
  return Array.from({ length: roleCount }, (_, index) => index);
}

export function isTemplateRoleBaseline(baseline: string): boolean {
  return baseline.startsWith(TEMPLATE_BASELINE_PREFIX);
}

export function getTemplateNameFromBaseline(baseline: string): string | null {
  if (!isTemplateRoleBaseline(baseline)) return null;
  const name = baseline.slice(TEMPLATE_BASELINE_PREFIX.length);
  return name || null;
}

export function serializeSessionRoleIndices(indices: number[]): string {
  const sorted = [...indices].sort((a, b) => a - b);
  if (sorted.length === 0) {
    return DEFAULT_ROLE_BASELINE;
  }
  return `${SESSION_BASELINE_PREFIX}${sorted.join(',')}`;
}

export function parseSessionRoleIndices(
  baseline: string,
  roleCount: number
): number[] {
  if (!baseline.startsWith(SESSION_BASELINE_PREFIX)) {
    return allSessionRoleIndices(roleCount);
  }

  const payload = baseline.slice(SESSION_BASELINE_PREFIX.length);
  if (payload === 'merged' || payload === '') {
    return allSessionRoleIndices(roleCount);
  }

  if (payload.includes(',')) {
    const parsed = payload
      .split(',')
      .map((part) => Number.parseInt(part.trim(), 10))
      .filter(
        (index) => !Number.isNaN(index) && index >= 0 && index < roleCount
      );
    return parsed.length > 0
      ? [...new Set(parsed)].sort((a, b) => a - b)
      : allSessionRoleIndices(roleCount);
  }

  const index = Number.parseInt(payload, 10);
  if (!Number.isNaN(index) && index >= 0 && index < roleCount) {
    return [index];
  }

  return allSessionRoleIndices(roleCount);
}

export function areAllSessionRolesSelected(
  indices: number[],
  roleCount: number
): boolean {
  if (roleCount === 0) return true;
  if (indices.length !== roleCount) return false;
  const expected = allSessionRoleIndices(roleCount);
  return expected.every((index) => indices.includes(index));
}

export function isDefaultRoleBaseline(
  baseline: string,
  roleCount: number
): boolean {
  if (isTemplateRoleBaseline(baseline)) return false;
  if (baseline === DEFAULT_ROLE_BASELINE) return true;
  if (roleCount <= 1) {
    return (
      baseline === DEFAULT_ROLE_BASELINE ||
      baseline === `${SESSION_BASELINE_PREFIX}0`
    );
  }
  return areAllSessionRolesSelected(
    parseSessionRoleIndices(baseline, roleCount),
    roleCount
  );
}

export function formatSessionRoleBaselineLabel(
  baseline: string,
  roles: UserRoles | null | undefined,
  properties: Property[] | undefined
): string {
  if (isTemplateRoleBaseline(baseline)) {
    const templateName = getTemplateNameFromBaseline(baseline);
    return templateName
      ? (getRoleTemplateLabel(templateName) ?? templateName)
      : 'Role template';
  }

  const roleCount = roles?.length ?? 0;
  if (isDefaultRoleBaseline(baseline, roleCount)) {
    return formatDefaultSessionRolesLabel(roles, properties);
  }

  const indices = parseSessionRoleIndices(baseline, roleCount);
  const labels = indices
    .map((index) => roles?.[index])
    .filter(Boolean)
    .map((role) => formatSessionRoleAssignment(role!, properties));

  if (labels.length === 0) {
    return formatDefaultSessionRolesLabel(roles, properties);
  }
  if (labels.length === 1) return labels[0];
  if (labels.length <= 2) return `${labels.join(' + ')} (merged)`;
  return `${labels[0]} + ${labels.length - 1} more roles (merged)`;
}

export function resolveSessionRolesFromBaseline(
  baseline: string,
  roles: UserRoles | null | undefined
): UserRoles {
  const list = roles ?? [];
  if (list.length === 0) return list;

  if (isDefaultRoleBaseline(baseline, list.length)) {
    return list;
  }

  const indices = parseSessionRoleIndices(baseline, list.length);
  return indices
    .map((index) => list[index])
    .filter((role): role is NonNullable<UserRoles>[number] => Boolean(role));
}
